import { useQuery } from '@tanstack/react-query';

import { supabase } from '@/lib/supabase';

export const reportKeys = {
  all: ['reports'] as const,
  funnel: () => ['reports', 'funnel'] as const,
  agents: () => ['reports', 'agents'] as const,
  teams: () => ['reports', 'teams'] as const,
  sla: () => ['reports', 'sla'] as const,
  forecast: () => ['reports', 'forecast'] as const,
  leakage: () => ['reports', 'leakage'] as const,
  activity: () => ['reports', 'activity'] as const,
  kpis: () => ['reports', 'kpis'] as const,
};

/**
 * The reporting views are not in the generated schema types, so PostgREST hands
 * them back as `any`. Narrowing through `unknown` in one helper keeps the row
 * interfaces below as the single description of each shape.
 */
const rows = <T>(data: unknown): T[] => (data ?? []) as T[];

export interface FunnelRow {
  stage: string;
  stage_order: number;
  reached: number;
  currently_here: number;
  lost_here: number;
  all_leads: number;
  pct_of_all: string | null;
}

export interface AgentScorecard {
  agent_id: string;
  agent_name: string;
  team_id: string | null;
  leads: number;
  contacted: number;
  qualified: number;
  visited: number;
  won: number;
  lost: number;
  never_contacted: number;
  overdue: number;
  going_cold: number;
  calls: number;
  whatsapp: number;
  meetings: number;
  visits: number;
  reservations: number;
  sales: number;
  revenue: string;
  agent_commission: string;
  sla_compliance_pct: string | null;
  median_response_seconds: string | null;
  close_rate: string | null;
  contact_rate: string | null;
  target_revenue: string | null;
  revenue_vs_target: string | null;
}

export interface TeamScorecard {
  team_id: string;
  team_name: string;
  agents: number;
  leads: number;
  qualified: number;
  visited: number;
  reservations: number;
  sales: number;
  revenue: string;
  never_contacted: number;
  overdue: number;
  close_rate: string | null;
  revenue_vs_target: string | null;
}

export interface ForecastRow {
  stage: string;
  stage_order: number;
  probability: string;
  leads: number;
  leads_without_value: number;
  pipeline_value: string;
  weighted_value: string;
}

export interface LeakageRow {
  lead_id: string;
  full_name: string;
  phone_primary: string;
  owner_name: string | null;
  stage: string;
  leak_type: string;
  hours_since: string;
  captured_at: string;
  last_activity_at: string | null;
}

export interface ActivityRow {
  activity_date: string;
  actor_id: string;
  actor_name: string;
  calls: number;
  calls_answered: number;
  whatsapp: number;
  meetings: number;
  visits: number;
  notes: number;
  leads_touched: number;
  talk_seconds: number | null;
}

export interface MonthlyKpi {
  month: string;
  leads: number;
  contacted: number;
  qualified: number;
  visited: number;
  reservations: number;
  sales: number;
  cancellations: number;
  revenue: string;
  commission: string;
  marketing_spend: string;
  sla_met: number;
  sla_breached: number;
  close_rate: string | null;
  cpl: string | null;
  roas: string | null;
}

/**
 * Every dashboard query runs the same SQL for everyone. The views are
 * `security_invoker`, so RLS on leads, deals and tasks decides what comes back —
 * which is what makes "a team_leader dashboard shows only their team" true
 * without a single filter in this file.
 */
const view =
  <T>(name: string, order?: { column: string; ascending?: boolean }) =>
  async (): Promise<T[]> => {
    let query = supabase.from(name).select('*');
    if (order) query = query.order(order.column, { ascending: order.ascending ?? true });
    const { data, error } = await query;
    if (error) throw error;
    return rows<T>(data);
  };

export function useFunnel() {
  return useQuery({
    queryKey: reportKeys.funnel(),
    queryFn: view<FunnelRow>('v_lead_funnel', { column: 'stage_order' }),
  });
}

export function useAgentScorecard() {
  return useQuery({
    queryKey: reportKeys.agents(),
    queryFn: view<AgentScorecard>('v_agent_scorecard', { column: 'revenue', ascending: false }),
  });
}

export function useTeamScorecard() {
  return useQuery({
    queryKey: reportKeys.teams(),
    queryFn: view<TeamScorecard>('v_team_scorecard', { column: 'revenue', ascending: false }),
  });
}

export function useForecast() {
  return useQuery({
    queryKey: reportKeys.forecast(),
    queryFn: view<ForecastRow>('v_pipeline_forecast', { column: 'stage_order' }),
  });
}

export function useLeakage() {
  return useQuery({
    queryKey: reportKeys.leakage(),
    queryFn: view<LeakageRow>('v_lead_leakage', { column: 'hours_since', ascending: false }),
  });
}

export function useDailyActivity() {
  return useQuery({
    queryKey: reportKeys.activity(),
    queryFn: view<ActivityRow>('v_daily_activity', { column: 'activity_date', ascending: false }),
  });
}

export function useMonthlyKpis() {
  return useQuery({
    queryKey: reportKeys.kpis(),
    queryFn: view<MonthlyKpi>('v_monthly_kpis', { column: 'month' }),
  });
}
